// ─────────────────────────────────────────────────────────────────────────────
// INPUT HANDLER
// Tracks keyboard state and exports a live InputState object.
// Mobile touch values are written here too (from MobileControls component).
// ─────────────────────────────────────────────────────────────────────────────

export type InputState = {
  left:  boolean;
  right: boolean;
  jump:  boolean;
  action: boolean;
  // Raw joystick axis [-1, 1] from touch controls
  touchX: number;
  touchJump: boolean;
  touchAction: boolean;
};

const state: InputState = {
  left:        false,
  right:       false,
  jump:        false,
  action:      false,
  touchX:      0,
  touchJump:   false,
  touchAction: false,
};

// ─── Keyboard ────────────────────────────────────────────────────────────────
const KEYS_LEFT   = new Set(['ArrowLeft',  'a', 'A']);
const KEYS_RIGHT  = new Set(['ArrowRight', 'd', 'D']);
const KEYS_JUMP   = new Set(['ArrowUp',    'w', 'W', ' ']);
const KEYS_ACTION = new Set(['e', 'E']);

let jumpConsumed   = false;   // edge-trigger: fire once per press
let actionConsumed = false;

function onKeyDown(e: KeyboardEvent) {
  if (KEYS_LEFT.has(e.key))   { state.left   = true; e.preventDefault(); }
  if (KEYS_RIGHT.has(e.key))  { state.right  = true; e.preventDefault(); }
  if (KEYS_JUMP.has(e.key))   { state.jump   = true; e.preventDefault(); }
  if (KEYS_ACTION.has(e.key)) { state.action = true; e.preventDefault(); }
}

function onKeyUp(e: KeyboardEvent) {
  if (KEYS_LEFT.has(e.key))   state.left   = false;
  if (KEYS_RIGHT.has(e.key))  state.right  = false;
  if (KEYS_JUMP.has(e.key))   { state.jump   = false; jumpConsumed   = false; }
  if (KEYS_ACTION.has(e.key)) { state.action = false; actionConsumed = false; }
}

export function initInput(): () => void {
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup',   onKeyUp);
  return () => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup',   onKeyUp);
    state.left        = false;
    state.right       = false;
    state.jump        = false;
    state.action      = false;
    state.touchX      = 0;
    state.touchJump   = false;
    state.touchAction = false;
    jumpConsumed   = false;
    actionConsumed = false;
  };
}

// ─── Accessors ───────────────────────────────────────────────────────────────

/** Composite left/right intent in [-1, 1] */
export function getAxisX(): number {
  const kb = (state.right ? 1 : 0) - (state.left ? 1 : 0);
  if (kb !== 0) return kb;
  if (Math.abs(state.touchX) > 0.2) return state.touchX > 0 ? 1 : -1;
  return 0;
}

/**
 * Returns true ONCE per jump-button press (edge trigger).
 * Must be polled every frame; resets automatically on button release.
 */
export function consumeJump(): boolean {
  const pressed = state.jump || state.touchJump;
  if (pressed && !jumpConsumed) {
    jumpConsumed = true;
    return true;
  }
  if (!pressed) jumpConsumed = false;
  return false;
}

/** Whether the jump button is currently held (for variable height). */
export function jumpHeld(): boolean {
  return state.jump || state.touchJump;
}

/** Action held (jetpack thrust / continuous). */
export function actionHeld(): boolean {
  return state.action || state.touchAction;
}

/** Action edge trigger (talk to NPC, pickups). */
export function consumeAction(): boolean {
  const pressed = state.action || state.touchAction;
  if (pressed && !actionConsumed) {
    actionConsumed = true;
    return true;
  }
  if (!pressed) actionConsumed = false;
  return false;
}

// ─── Mobile writers (called from MobileControls) ─────────────────────────────
export function setTouchAxis(x: number)      { state.touchX      = x; }
export function setTouchJump(v: boolean)     { state.touchJump   = v; }
export function setTouchAction(v: boolean)   { state.touchAction = v; }

/** Detect touch / coarse pointer for on-screen control hints. */
export function isTouchDevice(): boolean {
  return typeof navigator !== 'undefined' &&
    (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);
}
